import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { axiosLogistics } from '../axios/api';

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchRoadVendors = createAsyncThunk(
  'roadVendors/fetchRoadVendors',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v !== undefined && v !== '') params.append(k, v); });
      const res = await axiosLogistics.get(`/logistics/road-vendors?${params}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch road vendors');
    }
  }
);

export const fetchRoadVendorById = createAsyncThunk(
  'roadVendors/fetchRoadVendorById',
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.get(`/logistics/road-vendors/${id}`);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch road vendor');
    }
  }
);

export const createRoadVendor = createAsyncThunk(
  'roadVendors/createRoadVendor',
  async (vendorData, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post('/logistics/road-vendors', vendorData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data || { message: 'Failed to create road vendor' });
    }
  }
);

export const updateRoadVendor = createAsyncThunk(
  'roadVendors/updateRoadVendor',
  async ({ id, vendorData }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.put(`/logistics/road-vendors/${id}`, vendorData);
      return res.data.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update road vendor');
    }
  }
);

export const deleteRoadVendor = createAsyncThunk(
  'roadVendors/deleteRoadVendor',
  async (id, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/road-vendors/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to delete road vendor');
    }
  }
);

// ─── Origin Thunks ───────────────────────────────────────────────────────────

export const addRoadVendorOrigin = createAsyncThunk(
  'roadVendors/addOrigin',
  async ({ vendorId, state, city }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/road-vendors/${vendorId}/origins`, { state, city });
      return { vendorId, origin: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add origin');
    }
  }
);

export const removeRoadVendorOrigin = createAsyncThunk(
  'roadVendors/removeOrigin',
  async ({ vendorId, originId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/road-vendors/${vendorId}/origins/${originId}`);
      return { vendorId, originId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove origin');
    }
  }
);

// ─── Destination Thunks ───────────────────────────────────────────────────────

export const addRoadVendorDestination = createAsyncThunk(
  'roadVendors/addDestination',
  async ({ vendorId, destination_city, destination_state }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/road-vendors/${vendorId}/destinations`, { destination_city, destination_state });
      return { vendorId, destination: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add destination');
    }
  }
);

export const removeRoadVendorDestination = createAsyncThunk(
  'roadVendors/removeDestination',
  async ({ vendorId, destinationId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/road-vendors/${vendorId}/destinations/${destinationId}`);
      return { vendorId, destinationId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove destination');
    }
  }
);

// ─── Contact Thunks ──────────────────────────────────────────────────────────

export const addRoadVendorContact = createAsyncThunk(
  'roadVendors/addContact',
  async ({ vendorId, name, phone, email, position }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/road-vendors/${vendorId}/contacts`, { name, phone, email, position });
      return { vendorId, contact: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add contact');
    }
  }
);

export const updateRoadVendorContact = createAsyncThunk(
  'roadVendors/updateContact',
  async ({ vendorId, contactId, name, phone, email, position }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.put(`/logistics/road-vendors/${vendorId}/contacts/${contactId}`, { name, phone, email, position });
      return { vendorId, contact: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update contact');
    }
  }
);

export const removeRoadVendorContact = createAsyncThunk(
  'roadVendors/removeContact',
  async ({ vendorId, contactId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/road-vendors/${vendorId}/contacts/${contactId}`);
      return { vendorId, contactId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove contact');
    }
  }
);

// ─── Contact Detail Thunks (multiple phone/email pairs per contact) ──────────

export const addRoadVendorContactDetail = createAsyncThunk(
  'roadVendors/addContactDetail',
  async ({ vendorId, contactId, phone, email }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.post(`/logistics/road-vendors/${vendorId}/contacts/${contactId}/details`, { phone, email });
      return { vendorId, contactId, detail: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to add contact detail');
    }
  }
);

export const updateRoadVendorContactDetail = createAsyncThunk(
  'roadVendors/updateContactDetail',
  async ({ vendorId, contactId, detailId, phone, email }, { rejectWithValue }) => {
    try {
      const res = await axiosLogistics.put(`/logistics/road-vendors/${vendorId}/contacts/${contactId}/details/${detailId}`, { phone, email });
      return { vendorId, contactId, detail: res.data.data };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to update contact detail');
    }
  }
);

export const removeRoadVendorContactDetail = createAsyncThunk(
  'roadVendors/removeContactDetail',
  async ({ vendorId, contactId, detailId }, { rejectWithValue }) => {
    try {
      await axiosLogistics.delete(`/logistics/road-vendors/${vendorId}/contacts/${contactId}/details/${detailId}`);
      return { vendorId, contactId, detailId };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to remove contact detail');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState = {
  vendors: [],
  selectedVendor: null,
  pagination: { currentPage: 1, totalPages: 1, totalCount: 0, limit: 50 },
  filters: { page: 1, limit: 50, search: '', vendor_status: '', vendor_type: '', from_state: '', from_city: '', to_state: '', to_city: '' },
  loading: false, creating: false, updating: false, deleting: false,
  locationLoading: false,
  error: null,
};

const roadVendorSlice = createSlice({
  name: 'roadVendors',
  initialState,
  reducers: {
    clearSelectedRoadVendor: (state) => { state.selectedVendor = null; },
    updateRoadFilters: (state, action) => { state.filters = { ...state.filters, ...action.payload }; },
    resetRoadFilters:  (state) => { state.filters = initialState.filters; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRoadVendors.pending,   (state) => { state.loading = true; state.error = null; })
      .addCase(fetchRoadVendors.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.vendors = payload.vendors;
        state.pagination = payload.pagination;
      })
      .addCase(fetchRoadVendors.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(fetchRoadVendorById.pending,   (state) => { state.loading = true; })
      .addCase(fetchRoadVendorById.fulfilled, (state, { payload }) => { state.loading = false; state.selectedVendor = payload; })
      .addCase(fetchRoadVendorById.rejected,  (state, { payload }) => { state.loading = false; state.error = payload; })

      .addCase(createRoadVendor.pending,   (state) => { state.creating = true; state.error = null; })
      .addCase(createRoadVendor.fulfilled, (state, { payload }) => {
        state.creating = false;
        state.vendors.unshift(payload);
        state.pagination.totalCount += 1;
      })
      .addCase(createRoadVendor.rejected,  (state, { payload }) => { state.creating = false; state.error = payload; })

      .addCase(updateRoadVendor.pending,   (state) => { state.updating = true; state.error = null; })
      .addCase(updateRoadVendor.fulfilled, (state, { payload }) => {
        state.updating = false;
        const i = state.vendors.findIndex(v => v.id === payload.id);
        if (i !== -1) state.vendors[i] = payload;
        if (state.selectedVendor?.id === payload.id) state.selectedVendor = { ...state.selectedVendor, ...payload };
      })
      .addCase(updateRoadVendor.rejected,  (state, { payload }) => { state.updating = false; state.error = payload; })

      .addCase(deleteRoadVendor.pending,   (state) => { state.deleting = true; })
      .addCase(deleteRoadVendor.fulfilled, (state, { payload }) => {
        state.deleting = false;
        state.vendors = state.vendors.filter(v => v.id !== payload);
        state.pagination.totalCount -= 1;
        if (state.selectedVendor?.id === payload) state.selectedVendor = null;
      })
      .addCase(deleteRoadVendor.rejected,  (state, { payload }) => { state.deleting = false; state.error = payload; })

      // Origins
      .addCase(addRoadVendorOrigin.pending,   (state) => { state.locationLoading = true; })
      .addCase(addRoadVendorOrigin.fulfilled, (state, { payload }) => {
        state.locationLoading = false;
        if (state.selectedVendor?.id === payload.vendorId) {
          if (!state.selectedVendor.origins) state.selectedVendor.origins = [];
          state.selectedVendor.origins.push(payload.origin);
        }
      })
      .addCase(addRoadVendorOrigin.rejected,  (state) => { state.locationLoading = false; })

      .addCase(removeRoadVendorOrigin.pending,   (state) => { state.locationLoading = true; })
      .addCase(removeRoadVendorOrigin.fulfilled, (state, { payload }) => {
        state.locationLoading = false;
        if (state.selectedVendor?.id === payload.vendorId) {
          state.selectedVendor.origins = (state.selectedVendor.origins || []).filter(o => o.id !== payload.originId);
        }
      })
      .addCase(removeRoadVendorOrigin.rejected,  (state) => { state.locationLoading = false; })

      // Destinations
      .addCase(addRoadVendorDestination.pending,   (state) => { state.locationLoading = true; })
      .addCase(addRoadVendorDestination.fulfilled, (state, { payload }) => {
        state.locationLoading = false;
        if (state.selectedVendor?.id === payload.vendorId) {
          if (!state.selectedVendor.destinations) state.selectedVendor.destinations = [];
          state.selectedVendor.destinations.push(payload.destination);
        }
      })
      .addCase(addRoadVendorDestination.rejected,  (state) => { state.locationLoading = false; })

      .addCase(removeRoadVendorDestination.pending,   (state) => { state.locationLoading = true; })
      .addCase(removeRoadVendorDestination.fulfilled, (state, { payload }) => {
        state.locationLoading = false;
        if (state.selectedVendor?.id === payload.vendorId) {
          state.selectedVendor.destinations = (state.selectedVendor.destinations || []).filter(d => d.id !== payload.destinationId);
        }
      })
      .addCase(removeRoadVendorDestination.rejected,  (state) => { state.locationLoading = false; })

      // Contacts
      .addCase(addRoadVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          if (!state.selectedVendor.contacts) state.selectedVendor.contacts = [];
          state.selectedVendor.contacts.push(payload.contact);
        }
      })
      .addCase(updateRoadVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const i = (state.selectedVendor.contacts || []).findIndex(c => c.id === payload.contact.id);
          if (i !== -1) state.selectedVendor.contacts[i] = payload.contact;
        }
      })
      .addCase(removeRoadVendorContact.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          state.selectedVendor.contacts = (state.selectedVendor.contacts || []).filter(c => c.id !== payload.contactId);
        }
      })

      // Contact details
      .addCase(addRoadVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) { if (!c.details) c.details = []; c.details.push(payload.detail); }
        }
      })
      .addCase(updateRoadVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) {
            const i = (c.details || []).findIndex(d => d.id === payload.detail.id);
            if (i !== -1) c.details[i] = payload.detail;
          }
        }
      })
      .addCase(removeRoadVendorContactDetail.fulfilled, (state, { payload }) => {
        if (state.selectedVendor?.id === payload.vendorId) {
          const c = (state.selectedVendor.contacts || []).find(c => c.id === payload.contactId);
          if (c) c.details = (c.details || []).filter(d => d.id !== payload.detailId);
        }
      });
  },
});

export const { clearSelectedRoadVendor, updateRoadFilters, resetRoadFilters } = roadVendorSlice.actions;

export const selectRoadVendors             = (state) => state.roadVendors.vendors;
export const selectSelectedRoadVendor      = (state) => state.roadVendors.selectedVendor;
export const selectRoadVendorOrigins       = (state) => state.roadVendors.selectedVendor?.origins || [];
export const selectRoadVendorDestinations  = (state) => state.roadVendors.selectedVendor?.destinations || [];
export const selectRoadVendorContacts      = (state) => state.roadVendors.selectedVendor?.contacts || [];
export const selectRoadPagination          = (state) => state.roadVendors.pagination;
export const selectRoadFilters             = (state) => state.roadVendors.filters;
export const selectRoadLoading             = (state) => state.roadVendors.loading;
export const selectRoadCreating            = (state) => state.roadVendors.creating;
export const selectRoadUpdating            = (state) => state.roadVendors.updating;
export const selectRoadLocationLoading     = (state) => state.roadVendors.locationLoading;

export default roadVendorSlice.reducer;
